import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ApolloError } from '@apollo/client/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { QuillEditorComponent } from 'ngx-quill';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { filter } from 'rxjs';
import { SubSink } from 'subsink';
import { v4 as uuid } from 'uuid';

import { QuillTemplate, QuillTemplateLocation } from '../../../generated/graphql.generated';
import { FreyaCommonModule } from '../../freya-common/freya-common.module';
import { commentDefaultToolbar, isQuillContentsEmpty } from '../../shared/comments.utils';
import { TEMPLATE_VARIABLES } from '../../shared/quill-modules/quill-templates.helpers';
import { VariableToolbarQuillModule } from '../../shared/quill-modules/variableToolbar.quillModule';
import { getApolloErrorMessage } from '../../utilities/errors.util';
import { TEMPLATES_WITH_SUBJECT, TemplatesActions } from '../state/templates.store';

@Component({
    selector: 'app-mutate-template',
    standalone: true,
    imports: [FreyaCommonModule, QuillEditorComponent],
    templateUrl: './mutate-template.component.html',
    styleUrl: './mutate-template.component.scss'
})
export class MutateTemplateComponent implements OnInit, OnDestroy {
    @ViewChild('editor', { static: false }) editor: QuillEditorComponent | undefined;
    @ViewChild('subjectEditor', { static: false }) subjectEditor: QuillEditorComponent | undefined;

    isEditMode = false;

    /**
     * The template context is deliberately left unset. Every other value here is
     * a genuine empty default, but a context picked on the user's behalf files
     * the template under a place they never chose — a silently wrong template is
     * worse than one that cannot be saved until a context is selected.
     */
    template: QuillTemplate = {
        id: null,
        name: '',
        contents: '',
        subjectContents: '',
        location: null,
    };

    templateLocations = Object.values(QuillTemplateLocation).map(location => ({
        label: location.replaceAll('_', ' ').toLocaleLowerCase(),
        value: location,
    }));

    modules = {
        toolbar: commentDefaultToolbar,
        variableToolbar: true,
    };

    templatesWithSubjectContents = TEMPLATES_WITH_SUBJECT;

    /** Set on blur, so a field only turns red once the user has left it. */
    touched = {
        name: false,
        location: false,
        contents: false,
    };

    saving = false;

    /** What the backend said when a save was rejected, shown beside the buttons. */
    errorMessage: string | null = null;

    /** Identifies the save in flight, so other saves' outcomes are ignored. */
    private requestId: string | null = null;

    private readonly locationValues = new Set<unknown>(Object.values(QuillTemplateLocation));

    private subs = new SubSink();

    constructor(
        public ref: DynamicDialogRef,
        public config: DynamicDialogConfig,
        private store: Store,
        private actions$: Actions,
    ) { }

    get nameMissing(): boolean {
        return !this.template.name?.trim();
    }

    get locationMissing(): boolean {
        return !this.locationValues.has(this.template.location);
    }

    get contentsMissing(): boolean {
        return isQuillContentsEmpty(this.template.contents);
    }

    /**
     * Labels of the required fields still to fill, for the hint beside the save
     * button — the button is disabled while this is non-empty, and a disabled
     * button with no explanation is its own bug report.
     */
    get missingRequiredFields(): string[] {
        return [
            this.nameMissing ? 'Name' : null,
            this.locationMissing ? 'Template Context' : null,
            this.contentsMissing ? 'Contents' : null,
        ].filter((label): label is string => Boolean(label));
    }

    get showSubject(): boolean {
        return this.templatesWithSubjectContents.has(this.template.location as QuillTemplateLocation);
    }

    ngOnInit(): void {
        const seed = this.config.data?.template as Partial<QuillTemplate> | undefined;

        if (seed) {
            // Merge over the defaults rather than replacing them. The dialog is
            // opened with only a location — sometimes not even that — and an
            // undefined name or contents would wipe the empty-string defaults
            // the form and its validation are written against.
            const provided = Object.entries(structuredClone(seed))
                .filter(([ , value ]) => value !== undefined);

            this.template = { ...this.template, ...Object.fromEntries(provided) };
            this.isEditMode = Boolean(this.template.id);
        }

        this.subs.sink = this.actions$.pipe(
            ofType(TemplatesActions.templateCreated, TemplatesActions.templateUpdated),
            filter(({ requestId }) => this.isOwnOutcome(requestId)),
        ).subscribe(() => {
            this.saving = false;
            this.requestId = null;
            this.ref.close();
        });

        this.subs.sink = this.actions$.pipe(
            ofType(TemplatesActions.templateCreationFailed, TemplatesActions.templateUpdateFailed),
            filter(({ requestId }) => this.isOwnOutcome(requestId)),
        ).subscribe(({ error }) => {
            this.saving = false;
            this.requestId = null;
            this.errorMessage = getApolloErrorMessage(error as ApolloError);
        });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    save(): void {
        if (this.missingRequiredFields.length || this.saving) { return; }

        this.errorMessage = null;
        this.saving = true;
        this.requestId = uuid();

        const name = (this.template.name ?? '').trim();
        const contents = JSON.stringify(this.template.contents);
        // Guarded by `locationMissing`, so this is a real context by now.
        const location = this.template.location as QuillTemplateLocation;
        // A subject belongs only to the contexts that send one, and an editor
        // left untouched holds an empty delta rather than nothing.
        const subjectContents = this.showSubject && !isQuillContentsEmpty(this.template.subjectContents)
            ? JSON.stringify(this.template.subjectContents)
            : null;

        if (this.isEditMode) {
            this.store.dispatch(TemplatesActions.templateUpdateRequested({
                input: {
                    ids: [ this.template.id ],
                    update: { name, contents, location, subjectContents },
                },
                requestId: this.requestId,
            }));
            return;
        }

        this.store.dispatch(TemplatesActions.templateCreationRequested({
            input: { name, contents, location, subjectContents },
            requestId: this.requestId,
        }));
    }

    cancel(): void {
        this.ref.close();
    }

    onEditorCreated() {
        this.onTemplateLocationChange({value: this.template.location, originalEvent: null,});
    }

    onTemplateLocationChange(event: DropdownChangeEvent) {
        if (!event.value) return;
        const templateVariables = TEMPLATE_VARIABLES[event.value];
        [this.editor, this.subjectEditor].filter(Boolean).forEach(editor => {
            const variableToolbar = editor?.quillEditor?.getModule('variableToolbar') as VariableToolbarQuillModule;
            variableToolbar?.updateVariables(templateVariables);
        });
    }

    private isOwnOutcome(requestId: string | undefined): boolean {
        return Boolean(this.requestId) && requestId === this.requestId;
    }
}
